import React, { useEffect, useState } from 'react';
import {
  AutoComplete,
  Button,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Typography,
  notification,
} from 'antd';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import UserAvatar from 'components/user-avatar';
import { VoteEntry, apiErrorText, displayName, formatScore } from './types';

const REASONS = [
  'Sửa lỗi nhập điểm',
  'Bù phiếu bị lỗi hệ thống',
  'Huỷ phiếu gian lận / tài khoản ảo',
  'Điểm thưởng theo thể lệ',
];

interface Props {
  entry: VoteEntry | null;
  /** Điểm mỗi phiếu của sự kiện — nút cộng nhanh theo số phiếu */
  scorePerVote: number;
  onClose: () => void;
  onDone: () => void;
}

/** Cộng / trừ điểm tay cho một người, bắt buộc ghi lý do (vào nhật ký). */
const AdjustScoreModal = ({ entry, scorePerVote, onClose, onDone }: Props) => {
  const [mode, setMode] = useState<'plus' | 'minus'>('plus');
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setMode('plus');
      setAmount(null);
      setReason('');
    }
  }, [entry]);

  const current = entry?.score ?? 0;
  const delta = (amount || 0) * (mode === 'plus' ? 1 : -1);
  const next = Math.max(0, current + delta);
  const unit = scorePerVote > 0 ? scorePerVote : 100;
  const valid = !!amount && amount > 0 && reason.trim().length >= 3;

  const submit = async () => {
    if (!entry || !valid) return;
    setSaving(true);
    try {
      await AxiosUtils.post(API.voteAdminAdjust(entry.id), { delta, reason: reason.trim() });
      notification.success({
        message: 'Đã lưu điều chỉnh',
        description: `${displayName(entry)}: ${formatScore(current)} → ${formatScore(next)} điểm`,
      });
      onDone();
    } catch (e) {
      notification.error({ message: 'Không thành công', description: apiErrorText(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!entry}
      title="Cộng / trừ điểm"
      onCancel={onClose}
      maskClosable={!amount && !reason}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          Huỷ
        </Button>,
        <Button
          key="ok"
          type="primary"
          danger={mode === 'minus'}
          loading={saving}
          disabled={!valid}
          onClick={submit}
        >
          Lưu điều chỉnh
        </Button>,
      ]}
    >
      {entry ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space size={12}>
            <UserAvatar src={entry.user?.avatar} size={40} />
            <div>
              <Typography.Text strong>{displayName(entry)}</Typography.Text>
              <div>
                <Typography.Text type="secondary">
                  @{entry.user?.username} · hiện có <b>{formatScore(current)}</b> điểm
                </Typography.Text>
              </div>
            </div>
          </Space>

          <Segmented
            block
            value={mode}
            onChange={(v) => setMode(v as 'plus' | 'minus')}
            options={[
              { label: 'Cộng điểm', value: 'plus' },
              { label: 'Trừ điểm', value: 'minus' },
            ]}
          />

          <div>
            <Space wrap size={8} style={{ marginBottom: 8 }}>
              {[1, 5, 10].map((n) => (
                <Button key={n} size="small" onClick={() => setAmount(n * unit)}>
                  {n} phiếu ({formatScore(n * unit)})
                </Button>
              ))}
            </Space>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={10_000_000}
              precision={0}
              value={amount}
              onChange={(v) => setAmount(typeof v === 'number' ? v : null)}
              placeholder="Số điểm"
              prefix={mode === 'plus' ? '+' : '−'}
              suffix="điểm"
              formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              parser={(v) => Number(`${v ?? ''}`.replace(/\./g, '')) as any}
            />
            {amount ? (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                Sau khi lưu: {formatScore(current)} → <b>{formatScore(next)}</b> điểm
                {current + delta < 0 ? ' (không xuống dưới 0)' : ''}
              </Typography.Text>
            ) : null}
          </div>

          <div>
            <Typography.Text>Lý do (bắt buộc, lưu vào lịch sử)</Typography.Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 6 }}
              value={reason}
              onChange={setReason}
              options={REASONS.map((value) => ({ value }))}
              placeholder="Chọn hoặc gõ lý do"
              maxLength={255}
            />
          </div>
        </Space>
      ) : null}
    </Modal>
  );
};

export default AdjustScoreModal;
